/**
 * useMediaPlayer - 音声/動画共通の再生フック
 *
 * 音声/動画とも HTMLMediaElement を操作する。
 * timeupdate で activeUtteranceId を自動更新する。
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { TimelineBlock } from '@/types/review';
import { buildUtteranceIndex, findUtteranceAtTime } from '@/utils/audioTimestamp';

const PLAYBACK_RATES = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

export type MediaKind = 'audio' | 'video';

export interface MediaPlayerState {
  isLoaded: boolean;
  isPlaying: boolean;
  isBuffering: boolean;
  loadError: string | null;
  currentTime: number;
  duration: number;
  playbackRate: number;
  activeUtteranceId: string | null;
  fileName: string | null;
  kind: MediaKind | null;
  sourceUrl: string | null;
}

export interface MediaPlayerControls {
  loadFile: (file: File, kind?: MediaKind) => void;
  loadUrl: (url: string, displayName?: string, kind?: MediaKind) => void;
  bindMediaElement: (element: HTMLMediaElement | null) => void;
  reset: () => void;
  togglePlayPause: () => void;
  seekToUtterance: (utteranceId: string) => void;
  seekToTime: (seconds: number) => void;
  seekToPrevUtterance: () => void;
  seekToNextUtterance: () => void;
  skipBackward: () => void;
  skipForward: () => void;
  cyclePlaybackRate: () => void;
}

function ensureAudible(media: HTMLMediaElement) {
  media.muted = false;
  if (media.volume === 0) media.volume = 1;
}

function inferMediaKind(file: File): MediaKind {
  if (file.type.startsWith('video/')) return 'video';
  const name = file.name.toLowerCase();
  if (/\.(mp4|webm|mov|m4v)$/.test(name)) return 'video';
  return 'audio';
}

function buildPlaybackError(mediaKind: MediaKind | null, error: unknown) {
  const label = mediaKind === 'video' ? '動画' : '音声';
  const message = error instanceof Error ? error.message : '';
  return message
    ? `${label}の再生を開始できませんでした: ${message}`
    : `${label}の再生を開始できませんでした`;
}

export function useMediaPlayer(blocks: TimelineBlock[]): [MediaPlayerState, MediaPlayerControls] {
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const boundElementRef = useRef<HTMLMediaElement | null>(null);
  const cleanupListenersRef = useRef<(() => void) | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const playbackRateRef = useRef(1.0);
  const mediaKindRef = useRef<MediaKind | null>(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [activeUtteranceId, setActiveUtteranceId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [kind, setKind] = useState<MediaKind | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);

  const utteranceIndex = useMemo(() => buildUtteranceIndex(blocks), [blocks]);

  const utteranceStartMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of utteranceIndex) {
      map.set(entry.id, entry.startSeconds);
    }
    return map;
  }, [utteranceIndex]);

  const indexRef = useRef(utteranceIndex);
  indexRef.current = utteranceIndex;

  const handleTimeUpdate = useCallback(() => {
    if (!mediaRef.current) return;
    const ct = mediaRef.current.currentTime;
    setCurrentTime(ct);
    const found = findUtteranceAtTime(indexRef.current, ct);
    setActiveUtteranceId(found ? found.id : null);
  }, []);

  const detachMedia = useCallback(() => {
    cleanupListenersRef.current?.();
    cleanupListenersRef.current = null;
    if (mediaRef.current) {
      mediaRef.current.pause();
    }
    mediaRef.current = null;
  }, []);

  const attachMedia = useCallback(
    (media: HTMLMediaElement, mediaKind: MediaKind) => {
      detachMedia();
      mediaRef.current = media;
      mediaKindRef.current = mediaKind;
      media.preload = mediaKind === 'video' ? 'metadata' : 'auto';
      media.playbackRate = playbackRateRef.current;
      ensureAudible(media);

      const handleLoadedMetadata = () => {
        setDuration(Number.isFinite(media.duration) ? media.duration : 0);
        setIsLoaded(true);
        setLoadError(null);
      };
      const handleError = () => {
        setIsLoaded(false);
        setIsPlaying(false);
        setIsBuffering(false);
        setLoadError(mediaKind === 'video' ? '動画ファイルを再生できませんでした' : '音声ファイルを再生できませんでした');
      };
      const handleEnded = () => {
        setIsPlaying(false);
        setIsBuffering(false);
      };
      const handlePause = () => setIsPlaying(false);
      const handlePlay = () => setIsPlaying(true);
      const handleSeeking = () => setIsBuffering(true);
      const handleWaiting = () => setIsBuffering(true);
      const handleReadyToPlay = () => setIsBuffering(false);

      media.addEventListener('loadedmetadata', handleLoadedMetadata);
      media.addEventListener('error', handleError);
      media.addEventListener('timeupdate', handleTimeUpdate);
      media.addEventListener('ended', handleEnded);
      media.addEventListener('pause', handlePause);
      media.addEventListener('play', handlePlay);
      media.addEventListener('seeking', handleSeeking);
      media.addEventListener('waiting', handleWaiting);
      media.addEventListener('stalled', handleWaiting);
      media.addEventListener('seeked', handleReadyToPlay);
      media.addEventListener('canplay', handleReadyToPlay);
      media.addEventListener('playing', handleReadyToPlay);

      cleanupListenersRef.current = () => {
        media.removeEventListener('loadedmetadata', handleLoadedMetadata);
        media.removeEventListener('error', handleError);
        media.removeEventListener('timeupdate', handleTimeUpdate);
        media.removeEventListener('ended', handleEnded);
        media.removeEventListener('pause', handlePause);
        media.removeEventListener('play', handlePlay);
        media.removeEventListener('seeking', handleSeeking);
        media.removeEventListener('waiting', handleWaiting);
        media.removeEventListener('stalled', handleWaiting);
        media.removeEventListener('seeked', handleReadyToPlay);
        media.removeEventListener('canplay', handleReadyToPlay);
        media.removeEventListener('playing', handleReadyToPlay);
      };

      if (media.readyState >= 1) {
        handleLoadedMetadata();
      } else {
        try {
          media.load();
        } catch {
          // jsdom does not implement media loading; real browsers do.
        }
      }
    },
    [detachMedia, handleTimeUpdate],
  );

  const playCurrentMedia = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    const playedKind = mediaKindRef.current;

    ensureAudible(media);
    setLoadError(null);
    try {
      const result = media.play();
      if (result && typeof result.catch === 'function') {
        result.catch((error: unknown) => {
          if (mediaRef.current !== media) return;
          setIsPlaying(false);
          setIsBuffering(false);
          setLoadError(buildPlaybackError(playedKind, error));
        });
      }
    } catch (error) {
      if (mediaRef.current !== media) return;
      setIsPlaying(false);
      setIsBuffering(false);
      setLoadError(buildPlaybackError(playedKind, error));
    }
  }, []);

  const reset = useCallback(() => {
    detachMedia();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    boundElementRef.current = null;
    mediaKindRef.current = null;
    setFileName(null);
    setKind(null);
    setSourceUrl(null);
    setIsPlaying(false);
    setIsBuffering(false);
    setCurrentTime(0);
    setDuration(0);
    setActiveUtteranceId(null);
    setIsLoaded(false);
    setLoadError(null);
  }, [detachMedia]);

  useEffect(() => {
    return () => {
      detachMedia();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
      boundElementRef.current = null;
      mediaKindRef.current = null;
    };
  }, [detachMedia]);

  const resetStateForLoad = useCallback((name: string, mediaKind: MediaKind) => {
    setFileName(name);
    setKind(mediaKind);
    setIsPlaying(false);
    setIsBuffering(false);
    setCurrentTime(0);
    setDuration(0);
    setActiveUtteranceId(null);
    setIsLoaded(false);
    setLoadError(null);
  }, []);

  const initAudio = useCallback(
    (src: string, name: string, isObjectUrl: boolean) => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = isObjectUrl ? src : null;
      setSourceUrl(src);
      resetStateForLoad(name, 'audio');

      const audio = new Audio();
      audio.src = src;
      attachMedia(audio, 'audio');
    },
    [attachMedia, resetStateForLoad],
  );

  const initVideo = useCallback(
    (src: string, name: string, isObjectUrl: boolean) => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = isObjectUrl ? src : null;
      detachMedia();
      resetStateForLoad(name, 'video');
      setSourceUrl(src);
    },
    [detachMedia, resetStateForLoad],
  );

  const loadFile = useCallback(
    (file: File, explicitKind?: MediaKind) => {
      const mediaKind = explicitKind ?? inferMediaKind(file);
      const url = URL.createObjectURL(file);
      if (mediaKind === 'video') initVideo(url, file.name, true);
      else initAudio(url, file.name, true);
    },
    [initAudio, initVideo],
  );

  const loadUrl = useCallback(
    (url: string, displayName?: string, explicitKind: MediaKind = 'audio') => {
      if (explicitKind === 'video') initVideo(url, displayName ?? '動画', false);
      else initAudio(url, displayName ?? '音声', false);
    },
    [initAudio, initVideo],
  );

  const bindMediaElement = useCallback(
    (element: HTMLMediaElement | null) => {
      if (!element) {
        const currentElement = boundElementRef.current;
        boundElementRef.current = null;
        if (currentElement && mediaRef.current === currentElement) {
          detachMedia();
        }
        return;
      }
      boundElementRef.current = element;
      attachMedia(element, element instanceof HTMLVideoElement ? 'video' : 'audio');
    },
    [attachMedia, detachMedia],
  );

  const togglePlayPause = useCallback(() => {
    if (!mediaRef.current) return;
    if (mediaRef.current.paused) {
      playCurrentMedia();
    } else {
      mediaRef.current.pause();
    }
  }, [playCurrentMedia]);

  const seekToUtterance = useCallback(
    (utteranceId: string) => {
      if (!mediaRef.current) return;
      const startSec = utteranceStartMap.get(utteranceId);
      if (startSec === undefined) return;
      mediaRef.current.currentTime = startSec;
      setIsBuffering(true);
      playCurrentMedia();
    },
    [playCurrentMedia, utteranceStartMap],
  );

  const seekToTime = useCallback((seconds: number) => {
    if (!mediaRef.current) return;
    setIsBuffering(true);
    mediaRef.current.currentTime = seconds;
  }, []);

  const seekToPrevUtterance = useCallback(() => {
    if (!mediaRef.current || indexRef.current.length === 0) return;
    const ct = mediaRef.current.currentTime;
    const idx = indexRef.current;
    let cur = -1;
    for (let i = idx.length - 1; i >= 0; i--) {
      const e = idx[i];
      if (e && e.startSeconds <= ct) { cur = i; break; }
    }
    const curEntry = cur >= 0 ? idx[cur] : undefined;
    const target = cur > 0 && curEntry && (ct - curEntry.startSeconds < 2) ? cur - 1 : Math.max(cur, 0);
    const targetEntry = idx[target];
    if (!targetEntry) return;
    mediaRef.current.currentTime = targetEntry.startSeconds;
    setIsBuffering(true);
    playCurrentMedia();
  }, [playCurrentMedia]);

  const seekToNextUtterance = useCallback(() => {
    if (!mediaRef.current || indexRef.current.length === 0) return;
    const ct = mediaRef.current.currentTime;
    const idx = indexRef.current;
    let cur = -1;
    for (let i = idx.length - 1; i >= 0; i--) {
      const e = idx[i];
      if (e && e.startSeconds <= ct) { cur = i; break; }
    }
    const target = Math.min(cur + 1, idx.length - 1);
    const targetEntry = idx[target];
    if (!targetEntry) return;
    mediaRef.current.currentTime = targetEntry.startSeconds;
    setIsBuffering(true);
    playCurrentMedia();
  }, [playCurrentMedia]);

  const skipBackward = useCallback(() => {
    if (!mediaRef.current) return;
    setIsBuffering(true);
    mediaRef.current.currentTime = Math.max(0, mediaRef.current.currentTime - 10);
  }, []);

  const skipForward = useCallback(() => {
    if (!mediaRef.current) return;
    setIsBuffering(true);
    mediaRef.current.currentTime = Math.min(mediaRef.current.duration || 0, mediaRef.current.currentTime + 10);
  }, []);

  const cyclePlaybackRate = useCallback(() => {
    setPlaybackRate((prev) => {
      const idx = PLAYBACK_RATES.indexOf(prev);
      const next = PLAYBACK_RATES[(idx + 1) % PLAYBACK_RATES.length] ?? 1.0;
      playbackRateRef.current = next;
      if (mediaRef.current) mediaRef.current.playbackRate = next;
      return next;
    });
  }, []);

  return [
    {
      isLoaded,
      isPlaying,
      isBuffering,
      loadError,
      currentTime,
      duration,
      playbackRate,
      activeUtteranceId,
      fileName,
      kind,
      sourceUrl,
    },
    {
      loadFile,
      loadUrl,
      bindMediaElement,
      reset,
      togglePlayPause,
      seekToUtterance,
      seekToTime,
      seekToPrevUtterance,
      seekToNextUtterance,
      skipBackward,
      skipForward,
      cyclePlaybackRate,
    },
  ];
}
